import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem } from "../lib/types.js";
import type { CollectionRefs } from "./CollectionPanel.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";

interface Props {
  projectId: string;
  refs: CollectionRefs;
  onOpenScene: (sceneId: string) => void;
}

const str = (v: unknown) => (typeof v === "string" ? v : "");
const splitNames = (v: unknown) => str(v).split(/[,;]/).map((s) => s.trim()).filter(Boolean);

type Draft = { storyDate: string; storyTime: string; locationId: string; sceneId: string; characters: string; tags: string; description: string };
const emptyDraft: Draft = { storyDate: "", storyTime: "", locationId: "", sceneId: "", characters: "", tags: "", description: "" };

/**
 * Visual Timeline — the project's events as a connected vertical timeline (a spine
 * with nodes and cards), in the author's curated order, with a side editor. Matches
 * the app's dark theme. Reorder with the up/down controls; edits save on blur.
 */
export function TimelinePanel({ projectId, refs, onOpenScene }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<CollectionItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);

  const sceneTitle = (id: string) => refs.scenes.find((s) => s.id === id)?.title ?? null;
  const locationName = (id: string) => refs.locations.find((l) => l.id === id)?.name ?? null;

  const load = useCallback(async () => {
    try {
      const res = await api.listCollection(projectId, "timeline");
      setItems(res.items);
      setSelectedId((cur) => cur ?? res.items[0]?.id ?? null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load timeline");
    }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(() => (items ?? []).find((i) => i.id === selectedId) ?? null, [items, selectedId]);
  useEffect(() => {
    if (selected) setDraft({
      storyDate: str(selected.storyDate), storyTime: str(selected.storyTime),
      locationId: str(selected.locationId), sceneId: str(selected.sceneId),
      characters: str(selected.characters), tags: str(selected.tags), description: str(selected.description)
    });
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((i) => !q || `${str(i.title)} ${str(i.storyDate)} ${str(i.description)} ${str(i.characters)}`.toLowerCase().includes(q));
  }, [items, query]);

  const create = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "timeline", { title: "New Event" });
      await load();
      setSelectedId(item.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add event");
    }
  };

  const save = async (patch: Partial<Draft> & { title?: string }) => {
    if (!selected) return;
    try {
      await api.updateCollectionItem("timeline", selected.id, patch);
      setItems((prev) => (prev ? prev.map((i) => (i.id === selected.id ? { ...i, ...patch } : i)) : prev));
    } catch { /* keep draft; next save retries */ }
  };

  const move = async (id: string, dir: -1 | 1) => {
    if (!items) return;
    const idx = items.findIndex((i) => i.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setItems(next);
    try { await api.reorderTimeline(projectId, next.map((i) => i.id)); }
    catch { await load(); toast.error("Couldn't reorder"); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.deleteCollectionItem("timeline", id);
      setItems((prev) => (prev ? prev.filter((i) => i.id !== id) : prev));
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const reorderable = !query;

  return (
    <div className="vtl">
      <div className="vtl-main">
        <div className="vtl-head">
          <input className="input" placeholder="Search timeline…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <button className="btn btn-sm btn-primary" onClick={() => void create()}><Icon name="plus" size={14} /> New event</button>
        </div>

        {items && filtered.length === 0 && (
          <p className="faint" style={{ fontSize: 12, padding: "10px 4px" }}>
            {items.length === 0 ? "No events yet. Add the key moments of your story and they'll appear on the timeline." : "No matches."}
          </p>
        )}

        <div className="vtl-track">
          {filtered.map((it, i) => {
            const chars = splitNames(it.characters);
            const loc = locationName(str(it.locationId));
            const active = it.id === selectedId;
            return (
              <div key={it.id} className={`vtl-row ${active ? "active" : ""}`}>
                <div className="vtl-rail" aria-hidden>
                  <span className="vtl-dot" />
                </div>
                <button className="vtl-card" onClick={() => setSelectedId(it.id)}>
                  <div className="vtl-when">
                    {str(it.storyDate) || "—"}{str(it.storyTime) ? ` · ${str(it.storyTime)}` : ""}
                  </div>
                  <div className="vtl-title">{str(it.title) || "Untitled event"}</div>
                  {str(it.description) && <div className="vtl-desc">{str(it.description).slice(0, 140)}{str(it.description).length > 140 ? "…" : ""}</div>}
                  {(chars.length > 0 || loc) && (
                    <div className="vtl-meta">
                      {loc && <span className="vtl-chip"><Icon name="map" size={10} /> {loc}</span>}
                      {chars.map((c) => <span key={c} className="vtl-chip"><Icon name="users" size={10} /> {c}</span>)}
                    </div>
                  )}
                </button>
                {reorderable && (
                  <div className="vtl-reorder">
                    <button className="vtl-arrow" title="Move earlier" disabled={i === 0} onClick={() => void move(it.id, -1)}><Icon name="chevronUp" size={13} /></button>
                    <button className="vtl-arrow" title="Move later" disabled={i === filtered.length - 1} onClick={() => void move(it.id, 1)}><Icon name="chevronDown" size={13} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <aside className="vtl-detail">
        {!selected ? (
          <div className="collection-detail-empty" style={{ height: "100%" }}>
            <Icon name="timeline" size={28} style={{ color: "var(--text-faint)" }} />
            <h2>Timeline</h2>
            <p className="muted">Select an event to edit it, or add a new one.</p>
          </div>
        ) : (
          <>
            <div className="vtl-detail-head">
              <input
                className="input vtl-detail-title"
                value={str(selected.title)}
                onChange={(e) => setItems((prev) => prev ? prev.map((i) => i.id === selected.id ? { ...i, title: e.target.value } : i) : prev)}
                onBlur={(e) => void save({ title: e.target.value.trim() || "Untitled event" })}
                placeholder="Event title"
              />
              <button className="btn btn-icon btn-ghost btn-sm" title="Delete event" onClick={() => setPendingDelete(selected)}><Icon name="trash" size={15} /></button>
            </div>
            <div className="vtl-grid">
              <label className="vtl-field"><span className="faint">Story date</span>
                <input className="input" value={draft.storyDate} onChange={(e) => setDraft((d) => ({ ...d, storyDate: e.target.value }))} onBlur={() => void save({ storyDate: draft.storyDate })} placeholder="Day 3, 1885, Spring…" />
              </label>
              <label className="vtl-field"><span className="faint">Time</span>
                <input className="input" value={draft.storyTime} onChange={(e) => setDraft((d) => ({ ...d, storyTime: e.target.value }))} onBlur={() => void save({ storyTime: draft.storyTime })} placeholder="Dusk, 14:00…" />
              </label>
              <label className="vtl-field"><span className="faint">Location</span>
                <select className="select" value={draft.locationId} onChange={(e) => { setDraft((d) => ({ ...d, locationId: e.target.value })); void save({ locationId: e.target.value }); }}>
                  <option value="">—</option>
                  {refs.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
              <label className="vtl-field"><span className="faint">Scene reference</span>
                <select className="select" value={draft.sceneId} onChange={(e) => { setDraft((d) => ({ ...d, sceneId: e.target.value })); void save({ sceneId: e.target.value }); }}>
                  <option value="">—</option>
                  {refs.scenes.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </label>
              <label className="vtl-field"><span className="faint">Characters involved</span>
                <input className="input" value={draft.characters} onChange={(e) => setDraft((d) => ({ ...d, characters: e.target.value }))} onBlur={() => void save({ characters: draft.characters })} placeholder="comma, separated" />
              </label>
              <label className="vtl-field"><span className="faint">Tags</span>
                <input className="input" value={draft.tags} onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))} onBlur={() => void save({ tags: draft.tags })} placeholder="comma, separated" />
              </label>
            </div>
            <label className="vtl-field"><span className="faint">Description</span>
              <textarea className="textarea vtl-desc-edit" value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} onBlur={() => void save({ description: draft.description })} placeholder="What happens in this event…" />
            </label>
            {draft.sceneId && (
              <button className="btn btn-sm btn-ghost" onClick={() => onOpenScene(draft.sceneId)}><Icon name="file" size={13} /> Open scene{sceneTitle(draft.sceneId) ? `: ${sceneTitle(draft.sceneId)}` : ""}</button>
            )}
          </>
        )}
      </aside>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete event"
          message={`Delete “${str(pendingDelete.title) || "Untitled event"}”? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
